import type { TimeFormat } from '../lib/time';

/**
 * Типы ответов API. Все метки времени — epoch ms UTC (число), все деньги — целые
 * копейки (`*Kop` / `*_kop`), рабочие часы — минуты от полуночи в поясе центра.
 */

export type Role = 'client' | 'specialist' | 'admin' | 'superadmin';

export type Location = {
  id: number;
  title: string;
  address: string;
  /** «вход со двора, 2 этаж» — то, чего нет в адресной строке. */
  comment: string;
  is_primary: number;
  is_active: number;
  sort: number;
};

/** День в часах работы организации. `d`: 0 — понедельник. Минуты от полуночи. */
export type WorkingDay = { d: number; on: number; from: number; to: number };

export type Center = {
  title: string;
  about: string;
  phone: string;
  email: string;
  site: string;
  /** Логотип: data:-URL готового квадрата или пустая строка. */
  logo: string;
  /** Часы работы организации — справочные, сетка записи строится не из них. */
  hours: WorkingDay[];
  adminContact: string;
  /** IANA-пояс центра. Всё форматирование времени идёт только через него. */
  tz: string;
  /** За сколько часов до приёма отмена ещё бесплатна. */
  cancelHours: number;
  /** Формат показа времени. Хранение и поля ввода от него не зависят — только показ. */
  timeFormat: TimeFormat;
  locations: Location[];
  botUsername: string | null;
  /** Когда бот напоминает о записи: организация выбирает сама, подпись на карточке — отсюда. */
  reminders: { dayBefore: boolean; hoursBefore: number };
};

export type TeamMember = {
  direction_id: number;
  direction_title: string;
  specialist_id: number;
  specialist_name: string;
  /** Ссылка на фото; пустая строка — показываем инициалы. */
  specialist_photo: string;
  since: number;
};

export type Child = {
  id: number;
  client_id: number;
  name: string;
  birth_year: number | null;
  note: string;
  created_at: number;
  team: TeamMember[];
};

export type AppointmentStatus =
  | 'held' | 'pending_payment' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

export type Appointment = {
  id: number;
  specialist_id: number;
  client_id: number;
  child_id: number;
  service_id: number;
  location_id: number | null;
  series_id: number | null;
  series_detached: number;
  starts_at_utc: number;
  ends_at_utc: number;
  status: AppointmentStatus;
  intake_json: string;
  price_kop: number;
  payout_kop: number;
  hold_expires_at: number | null;
  created_by: string;
  created_at: number;
  updated_at: number;
  cancelled_at: number | null;
  cancel_reason: string | null;
  rescheduled_from: number | null;
  /** Приходят join'ом в клиентском списке. */
  child_name?: string;
  service_title?: string;
  specialist_name?: string;
  direction_title?: string;
};

export type TenantOverview = {
  id: string;
  title: string;
  status: 'active' | 'suspended' | 'archived';
  createdAt: number;
  bot: { username: string | null; connected: boolean };
  subscription: { plan: string; status: string; trialEndsAt: number | null } | null;
  counts: { admins: number; specialists: number; children: number; appointments: number };
  /** Администраторы — отдельная сущность: приёмы они не ведут и в специалистах не значатся. */
  admins: { id: number; name: string; tgId: number }[];
  pendingInvite: { code: string; displayName: string | null; expiresAt: number | null } | null;
};

/**
 * Ответ `GET /api/me` — единственный запрос, который решает, какое приложение показать.
 * Поля приходят не все сразу: набор зависит от роли, поэтому разбирать их надо в том
 * порядке, что задан в `resolveEntry`.
 */
export type Me = {
  tenant: string;
  role: Role;
  isAdmin: boolean;
  readOnly: boolean;
  center: Center;

  /**
   * Вошёл по ссылке-приглашению: сначала POST /api/join, потом перезапрос /api/me.
   * Имя и ник приходят из Telegram — на экране приглашения их правят и подтверждают.
   */
  pendingJoin?: { code: string; suggestedName: string; suggestedUsername: string };

  isSuper?: boolean;
  /**
   * Владелец продукта вошёл в чужую организацию: он работает в её админке, а
   * полоса в шапке напоминает, что вход уже записан в её журнал.
   */
  ownerVisit?: { tenant: string; title: string };
  platform?: { tenants: TenantOverview[]; platformTenant: string | null };

  specialistId?: number;

  client?: {
    id: number;
    firstName: string;
    phone: string;
    hasConsent: boolean;
    phoneVerified: boolean;
  };
  children?: Child[];
  needsOnboarding?: boolean;
  upcoming?: Appointment[];
};

/** Куда отправлять человека после /api/me. Порядок разбора важен и задан брифом. */
export type Entry = 'join' | 'owner' | 'staff' | 'onboarding' | 'client';

export function resolveEntry(me: Me): Entry {
  if (me.pendingJoin) return 'join';
  // Визит владельца разбирается раньше платформы: он пришёл сюда работать
  // администратором организации, а не смотреть список организаций.
  if (me.ownerVisit) return 'staff';
  if (me.isSuper) return 'owner';
  if (me.role === 'admin' || me.role === 'specialist') return 'staff';
  if (me.needsOnboarding) return 'onboarding';
  return 'client';
}

export type Direction = { id: number; title: string; slug: string; sort: number; is_active: number };

export type ServiceRate = { serviceId: number; title: string; durationMin: number; priceKop: number; isOnline: boolean };

export type IntakeResponse = {
  service: { id: number; title: string; durationMin: number; isOnline: boolean } | null;
  specialists: { id: number; name: string; title: string; priceKop: number }[];
};

export type SlotDay = {
  date: string;
  slots: { time: string; startsAt: number; endsAt: number }[];
};

export type CreatedAppointment = { appointment: Appointment; holdExpiresAt: number | null };
